import { Utils, Logger, StringUtils } from "neo-common";
import { ClientHost, GameEngine, Platforms } from "neo-games";
import { Languages } from "../../../Utils/Languages";
import { FrameTimerEventEmitter, FrameTimerEvents, CreateFrameTimer } from "../../../Utils/FrameTimer";
import { AppContext } from "../../../../Common/AppContext";
import { FreeRoundBonus } from "../Bonuses/FreeRoundBonus";
import { GameApp } from "../../../GameApp";
import { ModuleManager } from "../../../Managers/ModuleManager";
import { BaseModel } from "../../../GameComponents/Base/BaseModel";
import { GameConfiguration } from "../../../../Common/GameConfiguration";
import { BuyFeature } from "../BuyFeature/BuyFeature";
import { BetHigherThanMinDialog } from "./BetHigherThanMinDialog";

export type DialogOpenData = {
    special: boolean;
    gameRoundId: string;
    errorId: string;
    actions: Array<Object>;
};

/**
 * Create dialog filled from code.
 * @class Dialog
 */
export class Dialog {
    static DisabledClass: string = "_disabled";
    static InfoClass: string = "info";
    static ErrorClass: string = "error";

    private _app: GameEngine.IApp;

    private _element: HTMLElement;
    private _elementHeading: HTMLElement;
    private _elementCode: HTMLElement;
    private _elementText: HTMLElement;
    private _elementButton: HTMLElement;
    private _elementButtonReload: HTMLElement;
    private _elementCustomButtons: HTMLElement;
    private _elementInfo: HTMLElement;
    private _elementError: HTMLElement;

    private _timer: FrameTimerEventEmitter;

    private _customData: any;

    constructor(app: GameEngine.IApp) {
        this._app = app;
        this._element = document.getElementById("dialog");
        this._elementHeading = document.getElementById("dialogHeading");
        this._elementCode = document.getElementById("errorCode");
        this._elementText = document.getElementById("dialogText");
        this._elementButton = document.getElementById("dialogButton");
        this._elementButtonReload = document.getElementById("dialogReloadButton");
        this._elementCustomButtons = document.getElementById("dialogCustomButtons");
        this._elementInfo = document.getElementById("dialogInfo");
        this._elementError = document.getElementById("dialogError");

        if (this._element != null && !this._element.classList.contains(Dialog.DisabledClass)) {
            this._element.classList.add(Dialog.DisabledClass);
        }

        if (this._elementInfo != null) {
            this._elementInfo.style.display = "none";
        }

        if (this._elementError != null) {
            this._elementError.style.display = "none";
        }

        this._timer = CreateFrameTimer("dialogTimer");
        this._timer.on(FrameTimerEvents.Complete, this.closeDialog.bind(this));

        this.onClickError = this.onClickError.bind(this);
        this.onClickRefresh = this.onClickRefresh.bind(this);

        this._customData = null;
    }

    openDialog(
        text: string,
        code: string = "",
        errorId: string = "",
        error: boolean = false,
        special: boolean = false,
        gameRoundId: string = "0",
        customData: any = null,
    ): void {
        this._customData = customData;

        if (DEBUG) {
            Logger.debugGroup(
                "Dialog",
                `openDialog('${text}', code: '${code}', errorId: '${errorId}', error: ${error}, special: ${special}, ` +
                    `gameRoundId: ${gameRoundId}, customData: ${customData ? JSON.stringify(customData) : ""})`,
            );
        }

        Platforms.Platforms.dialogOpened("default", true);

        if (this._element != null) {
            const ultraSpecial =
                !Utils.isNullOrUndefined(this._customData) &&
                !Utils.isNullOrUndefined(this._customData.data) &&
                !Utils.isNullOrUndefined(this._customData.data.ultraSpecial) &&
                this._customData.data.ultraSpecial === true;

            if (this._elementText != null) {
                this._elementText.innerHTML = text;
            }

            if (this._elementCode != null) {
                if (code !== "" && this._elementCode != null && !special) {
                    if (errorId === "") {
                        errorId = "0";
                    }

                    let errorString: string = gameRoundId + "-" + errorId;
                    code = "CODE_" + code.replace("-", "_");
                    let errorValue: string = Languages.getRid(code, errorString);
                    this._elementCode.textContent = errorValue.replace(/ /g, "");
                    this._elementCode.style.display = "block";
                } else {
                    this._elementCode.style.display = "none";
                }
            }

            if (error && !ultraSpecial) {
                if (this._element.classList.contains(Dialog.InfoClass)) {
                    this._element.classList.remove(Dialog.InfoClass);
                }

                if (!this._element.classList.contains(Dialog.ErrorClass)) {
                    this._element.classList.add(Dialog.ErrorClass);
                }

                if (this._elementInfo != null) {
                    this._elementInfo.style.display = "none";
                }
                if (this._elementError != null) {
                    this._elementError.style.display = "block";
                }

                if (this._elementHeading != null) {
                    this._elementHeading.textContent = Languages.getRid("DLG_ERROR");
                }

                if (this._elementButtonReload != null) {
                    this._elementButtonReload.style.display = "none";
                }

                if (this._elementButton != null) {
                    this._elementButton.style.display = "block";
                    this._elementButton.onclick = this.onClickError;
                }
            } else {
                if (!this._element.classList.contains(Dialog.InfoClass)) {
                    this._element.classList.add(Dialog.InfoClass);
                }

                if (this._element.classList.contains(Dialog.ErrorClass)) {
                    this._element.classList.remove(Dialog.ErrorClass);
                }

                if (this._elementInfo != null) {
                    this._elementInfo.style.display = "block";
                }
                if (this._elementError != null) {
                    this._elementError.style.display = "none";
                }

                if (this._elementHeading != null) {
                    this._elementHeading.textContent = Languages.getRid("DLG_INFO");
                }

                if (this._elementButton != null) {
                    this._elementButton.style.display = "none";
                }

                if (this._elementButtonReload) {
                    this._elementButtonReload.style.display = "none";
                }

                if (ultraSpecial) {
                    if (this._elementCode != null) {
                        this._elementCode.style.display = "none";
                    }

                    if (this._elementButton != null) {
                        this._elementButton.style.display = "block";
                        this._elementButton.onclick = this.onClickError;
                    }
                } else if (special) {
                    if (this._elementCode != null) {
                        this._elementCode.style.display = "none";
                    }

                    if (this._elementButtonReload != null) {
                        this._elementButtonReload.style.display = "block";
                        this._elementButtonReload.onclick = this.onClickRefresh;
                    }
                } else {
                    //in case, not enough credit dialog is already opened, send close dialog event
                    if (this._timer.isPlaying()) {
                        Platforms.Platforms.dialogOpened("default", false);
                    }

                    this._timer.stop();
                    this._timer.start(180);
                }
            }

            if (
                !Utils.isNullOrUndefined(this._customData.data) &&
                !Utils.isNullOrUndefined(this._customData.data.actions)
            ) {
                this.handleCustomActions(this._customData.data.actions);
            }

            if (this._element.classList.contains(Dialog.DisabledClass)) {
                this._element.classList.remove(Dialog.DisabledClass);
            }
        } else {
            if (DEBUG) {
                Logger.errorGroup("Dialog", "ERROR no dialog");
            }
        }
    }

    closeDialog(): void {
        if (DEBUG) {
            Logger.debugGroup("Dialog", `closeDialog()`);
        }

        this._timer.stop();

        Platforms.Platforms.dialogOpened("default", false);

        if (!this._element.classList.contains(Dialog.DisabledClass)) {
            this._element.classList.add(Dialog.DisabledClass);
        }

        if (FreeRoundBonus.id != null || FreeRoundBonus.otherOfferes.length > 0) {
            FreeRoundBonus.enableUi();
        }

        this.hideAllButtons();

        BetHigherThanMinDialog.setOpenedStatus();
    }

    private onClickError(): void {
        let clientHost = AppContext.clientHost as ClientHost.IClientHost;
        if (clientHost) {
            var request: ClientHost.Types.ClientHostEndGameRequest = null;
            if (!Utils.isNullOrUndefined(this._customData)) {
                let dialogRequest = this._customData as ClientHost.Types.ClientHostDialogRequest;

                request = dialogRequest.error as any;
                request.reason = dialogRequest.error.errorMessageTitle;
            }

            if (DEBUG) {
                Logger.debugGroup(
                    "Dialog",
                    `onClickError -> calling client host '${AppContext.gameDefinition.ClientHostType}.endGame()', request: `,
                    request,
                );
            }

            Platforms.Platforms.exitHandler = "dialog";
            Platforms.Platforms.gameClosing();
            clientHost.endGame(this._app, request);
        }
    }

    private onClickRefresh(): void {
        Platforms.Platforms.gameReloading();
        if (Platforms.Platforms.handleGameReload) {
            return;
        }

        window.location.reload(true);
    }

    private handleCustomActions(actions: Array<Object>): void {
        if (actions.length > 0) {
            this.hideAllButtons();

            //handle font size of custom text
            let buttons: Array<HTMLElement> = [];
            let imageWidthCorrection = 0.82; //transparent pixels correction
            let baseWidth = 0;
            let baseFontSize = 0;
            let maxWidth = 0;

            for (let i = 0; i < actions.length; i++) {
                let currentAction = actions[i];

                if (currentAction && currentAction["type"]) {
                    let button: HTMLElement = document.createElement("div");
                    button.classList.add("dialogBtn", "customDialogBtn");

                    //append child to get computed style
                    this._elementCustomButtons.appendChild(button);

                    baseWidth =
                        parseInt(window.getComputedStyle(button).getPropertyValue("width")) * imageWidthCorrection;
                    baseFontSize = parseInt(window.getComputedStyle(button).getPropertyValue("font-size"));
                    let currentWidth = 0;

                    if (currentAction["text"]) {
                        currentWidth = this.getButtonTextWidth(currentAction["text"], baseFontSize);
                        button.textContent = currentAction["text"];
                    }

                    if (currentWidth > maxWidth) {
                        maxWidth = currentWidth;
                    }

                    switch (currentAction["type"]) {
                        case "continue":
                            button.onclick = this.onClickContinue.bind(this);
                            break;
                        case "exit":
                            button.onclick = this.onClickClose;
                            break;
                        case "history":
                            button.onclick = this.onClickHistory;
                            break;
                        case "reload":
                            button.onclick = this.onClickReload;
                            break;
                        case "restore":
                            button.onclick = this.onClickRestore.bind(this, currentAction["customData"]);
                            break;
                    }

                    buttons.push(button);
                }
            }

            if (maxWidth > baseWidth) {
                let newFontSize = Math.round(baseFontSize * (baseWidth / maxWidth));

                for (let i = 0; i < buttons.length; i++) {
                    buttons[i].style.fontSize = newFontSize + "px";
                }
            }
        }
    }

    private getButtonTextWidth(text: string, fontSize: number): number {
        let elem = document.createElement("span");

        elem.textContent = text;
        elem.style.fontSize = fontSize + "px";
        document.body.appendChild(elem);
        let width = elem.offsetWidth;

        document.body.removeChild(elem);
        return width;
    }

    private hideAllButtons(): void {
        if (this._elementButton != null) {
            this._elementButton.style.display = "none";
        }

        if (this._elementButtonReload != null) {
            this._elementButtonReload.style.display = "none";
        }

        while (this._elementCustomButtons.firstChild) {
            this._elementCustomButtons.removeChild(this._elementCustomButtons.firstChild);
        }
    }

    private onClickClose(): void {
        Platforms.Platforms.exitHandler = "dialog";
        GameApp.exit();
    }

    private onClickContinue(): void {
        this.closeDialog();
        BaseModel.spinContinue();
    }

    private onClickHistory(): void {
        if (!StringUtils.isNullOrEmpty(AppContext.gameConfiguration.HistoryUrl)) {
            GameApp.openGameRoundHistoryFromUrl();
        } else if (!StringUtils.isNullOrEmpty(AppContext.gameConfiguration.RCIHistoryUrl)) {
            GameApp.openGameRoundHistoryFromUrl();
        } else {
            GameApp.openGameRoundHistory();
        }
    }

    private onClickReload(): void {
        Platforms.Platforms.gameReloading();
        if (Platforms.Platforms.handleGameReload) {
            return;
        }

        window.location.reload(true);
    }

    private onClickRestore(module: string): void {
        this.closeDialog();
        ModuleManager.restoreGameRound(module);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Dialogs/Dialog.ts
 **/